import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import AlertsBell from "@/components/AlertsBell";
import XpBadge from "@/components/XpBadge";
import WelcomeTour from "@/components/WelcomeTour";
import UpgradeDialog from "@/components/UpgradeDialog";
import { PLAN_NAMES } from "@/lib/plans";
import { ListIcon, SignOutIcon, XIcon, CaretDownIcon, GearSixIcon, CrownIcon } from "@phosphor-icons/react";

// Consolidated navigation: 4 flagship top-level links + 2 grouped dropdown menus.
const PRIMARY = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/watch", label: "Watch With Gamly", accent: true },
  { to: "/hot-takes", label: "Hot Takes", accent: true },
  { to: "/press-box", label: "Press Box", accent: true },
];

const GROUPS = [
  {
    label: "Games",
    items: [
      { to: "/games", label: "Today's Games" },
      { to: "/live", label: "Live Companion" },
      { to: "/why-it-matters", label: "Why It Matters" },
      { to: "/highlights", label: "Highlights" },
    ],
  },
  {
    label: "Studio",
    items: [
      { to: "/storylines", label: "Storylines" },
      { to: "/ask", label: "Ask Gamly" },
      { to: "/memory", label: "Fan Memory" },
      { to: "/cards", label: "Sports Cards" },
    ],
  },
];

const testId = (to) => `nav-${to.replace(/^\//, "").replace(/\//g, "-")}`;

// Classname helpers (extracted to avoid nested ternaries inline)
function navLinkClass(isActive, accent) {
  const base = "px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md transition-colors whitespace-nowrap";
  if (isActive && accent) return `${base} text-[#FDB927] bg-[#FDB927]/10`;
  if (isActive) return `${base} text-[#0A84FF] bg-white/5`;
  if (accent) return `${base} text-[#FDB927]/80 hover:text-[#FDB927]`;
  return `${base} text-white/60 hover:text-white`;
}

function mobileNavLinkClass(isActive, accent) {
  const base = "px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-md";
  if (isActive && accent) return `${base} text-[#FDB927] bg-[#FDB927]/10`;
  if (isActive) return `${base} text-[#0A84FF] bg-white/5`;
  if (accent) return `${base} text-[#FDB927]/80 bg-[#FDB927]/5`;
  return `${base} text-white/70 bg-white/5`;
}

function NavDropdown({ group }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const active = group.items.some(
    (i) => location.pathname === i.to || location.pathname.startsWith(`${i.to}/`)
  );

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const btnClass = active
    ? "text-[#0A84FF] bg-white/5"
    : "text-white/60 hover:text-white";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-group-${group.label.toLowerCase()}`}
        aria-expanded={open}
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md transition-colors whitespace-nowrap ${btnClass}`}
      >
        {group.label}
        <CaretDownIcon size={11} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid={`nav-group-menu-${group.label.toLowerCase()}`}
          className="absolute left-0 mt-1.5 min-w-[190px] rounded-md bg-[#121212] border border-white/10 shadow-2xl shadow-black/50 p-1 z-50"
        >
          {group.items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={testId(n.to)}
              className={({ isActive }) =>
                `block px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded transition-colors ${
                  isActive ? "text-[#0A84FF] bg-white/5" : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);

  const tourKey = user ? `gamly_tour_seen_v1_${user.user_id}` : null;
  useEffect(() => {
    if (user && tourKey && !localStorage.getItem(tourKey)) {
      setShowTour(true);
    }
  }, [user, tourKey]);

  const dismissTour = () => {
    if (tourKey) localStorage.setItem(tourKey, "1");
    setShowTour(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-[#050505]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <NavLink to="/dashboard" className="flex items-center gap-1.5 shrink-0 mr-6" data-testid="brand-logo">
            <img src="/gamly-logo.png" alt="Gamly AI" className="h-9 w-auto" />
            <span className="text-[9px] font-black uppercase tracking-[0.12em] text-[#0A84FF] bg-[#0A84FF]/12 border border-[#0A84FF]/30 rounded px-1 py-0.5 leading-none" data-testid="beta-badge">Beta</span>
          </NavLink>
          <nav className="hidden lg:flex items-center gap-0.5">
            {PRIMARY.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={testId(n.to)}
                className={({ isActive }) => navLinkClass(isActive, n.accent)}
              >
                {n.label}
              </NavLink>
            ))}
            {GROUPS.map((g) => (
              <NavDropdown key={g.label} group={g} />
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {user && user.plan !== "press_box_pro" && (
              <NavLink
                to={`/pricing?plan=${user.plan === "pro" ? "press_box_pro" : "pro"}`}
                data-testid="nav-upgrade-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#FDB927] hover:bg-yellow-400 text-black text-[11px] font-black uppercase tracking-wider transition-colors whitespace-nowrap"
              >
                <CrownIcon weight="fill" size={13} /> Upgrade
              </NavLink>
            )}
            <XpBadge />
            {user && <AlertsBell />}
            {user && (
              <div className="hidden sm:flex items-center gap-2">
                <Avatar src={user.picture} name={user.name} size={28} />
                <span className={`text-sm font-medium text-white/80 max-w-[120px] truncate ${user.plan !== "press_box_pro" ? "hidden 2xl:inline" : ""}`} title={user.name}>{user.name}</span>
                {user.plan && user.plan !== "free" && (
                  <span data-testid="nav-plan-badge" className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#32D74B]/15 text-[#32D74B] border border-[#32D74B]/40">
                    {PLAN_NAMES[user.plan] || user.plan}
                  </span>
                )}
              </div>
            )}
            <NavLink
              to="/settings"
              data-testid="nav-settings"
              className={({ isActive }) => `hidden lg:inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${isActive ? "text-[#0A84FF] bg-white/5" : "text-white/50 hover:text-white hover:bg-white/5"}`}
              title="Settings"
            >
              <GearSixIcon size={18} weight="bold" />
            </NavLink>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="hidden sm:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white"
            >
              <SignOutIcon size={16} /> Out
            </button>
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white"
            >
              {open ? <XIcon size={22} /> : <ListIcon size={22} />}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-white/10 bg-[#050505]" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {PRIMARY.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${testId(n.to)}`}
                    className={({ isActive }) => mobileNavLinkClass(isActive, n.accent)}
                  >
                    {n.label}
                  </NavLink>
                ))}
              </div>
              {GROUPS.map((g) => (
                <div key={g.label} data-testid={`mobile-group-${g.label.toLowerCase()}`}>
                  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/35 mb-1.5 px-1">{g.label}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {g.items.map((n) => (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-${testId(n.to)}`}
                        className={({ isActive }) => mobileNavLinkClass(isActive, false)}
                      >
                        {n.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2 pt-1 border-t border-white/10">
                <NavLink
                  to="/settings"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-settings"
                  className={({ isActive }) => mobileNavLinkClass(isActive, false)}
                >
                  Settings
                </NavLink>
                <button
                  onClick={handleLogout}
                  data-testid="mobile-logout-btn"
                  className="px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-md bg-[#FF453A]/15 text-[#FF453A]"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        )}
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">{children}</main>
      {showTour && <WelcomeTour onClose={dismissTour} />}
      <UpgradeDialog />
    </div>
  );
}
